import {
  ApiClient,
  ApiError,
  type RunpodCustomModel,
  type RunpodModelEntry,
  type RunpodModelLocation,
  type RunpodModelSection,
} from '../lib/api.js';
import { el, formatBytes } from '../lib/dom.js';

export interface ModelsEditorDeps {
  api: ApiClient;
  branch: string;
  /** Called after a successful save so the caller can refresh related state. */
  onSaved?: (info: { commitSha?: string; message?: string }) => void;
}

const LOCAL_PREFIX = 'local://';
type ItemSource = 'url' | 'local';

function urlSource(url: string): ItemSource {
  return url.startsWith(LOCAL_PREFIX) ? 'local' : 'url';
}

function localNameFromUrl(url: string): string {
  return url.startsWith(LOCAL_PREFIX) ? url.slice(LOCAL_PREFIX.length) : '';
}

const DEFAULT_FOLDERS = [
  'checkpoints',
  'text_encoders',
  'vae',
  'loras',
  'diffusion_models',
  'controlnet',
];

/** Mutable in-memory model entry the editor manipulates before save. */
interface EditorItem {
  filename: string;
  url: string;
  displayName: string;
  description: string;
  triggerWords: string;
  location: RunpodModelLocation;
}

/** Mutable in-memory section the editor manipulates before save. */
interface EditorSection {
  folder: string;
  items: EditorItem[];
}

/**
 * Opens a modal that lets the user view/edit the models declared in the
 * branch's Dockerfile. Sections map to /ComfyUI/models/<folder>/. Save commits
 * + pushes the regenerated Dockerfile via the GitHub API.
 */
export function openModelsEditor(deps: ModelsEditorDeps): void {
  const backdrop = el('div', { class: 'modal-backdrop' });
  const titleEl = el('h3', { text: `Models · ${deps.branch}` });
  const subtitleEl = el('span', { class: 'who', text: 'loading…' });
  const alertBox = el('div', { class: 'alert', attrs: { hidden: true, role: 'alert' } });
  const sectionsHost = el('div', { class: 'models-editor-sections' });
  const addSectionBtn = el('button', {
    class: 'ghost',
    text: '+ Add section',
    attrs: { type: 'button' },
  });
  const saveBtn = el('button', {
    class: 'primary',
    text: 'Save',
    attrs: { disabled: true },
  });
  const cancelBtn = el('button', { class: 'ghost', text: 'Close' });

  // CUDA architectures input — applies to both ENV TORCH_CUDA_ARCH_LIST lines
  // (builder + runtime stages) on save.
  const cudaInput = el('input', {
    class: 'mono',
    attrs: {
      type: 'text',
      placeholder: '8.6;8.9',
      spellcheck: false,
      autocapitalize: 'off',
      autocorrect: 'off',
      'aria-label': 'TORCH_CUDA_ARCH_LIST',
    },
  });
  cudaInput.addEventListener('input', () => {
    markDirty();
  });
  const cudaPanel = el('div', {
    class: 'models-editor-cuda',
    children: [
      el('span', {
        class: 'models-editor-cuda-label',
        text: 'TORCH_CUDA_ARCH_LIST',
      }),
      cudaInput,
      el('span', {
        class: 'who',
        text: 'separators: ; or whitespace · entries like 8.6, 8.9',
      }),
    ],
  });

  const modal = el('div', {
    class: 'modal modal-wide models-editor',
    children: [
      el('header', {
        children: [
          el('div', { class: 'modal-title', children: [titleEl, subtitleEl] }),
          el('div', { class: 'log-actions', children: [cancelBtn, saveBtn] }),
        ],
      }),
      alertBox,
      cudaPanel,
      sectionsHost,
      el('div', {
        class: 'models-editor-footer',
        children: [addSectionBtn],
      }),
    ],
  });
  backdrop.append(modal);
  document.body.append(backdrop);

  let baseSha = '';
  let sections: EditorSection[] = [];
  let dirty = false;
  // Cached list of locally-uploaded models (from /api/runpod-custom-models).
  // Populated alongside the Dockerfile load so the per-row "Local" picker
  // can render a dropdown of available uploads. Re-fetched on demand via
  // the small refresh button next to the dropdown.
  let localModels: RunpodCustomModel[] = [];

  const reloadLocalModels = async (): Promise<void> => {
    try {
      const res = await deps.api.listRunpodCustomModels();
      localModels = res.items;
    } catch (err) {
      // Non-fatal: editor still works with URL entries; the local picker
      // just shows an empty list with a hint to retry.
      localModels = [];
      // eslint-disable-next-line no-console
      console.warn('Failed to load custom models', err);
    }
  };

  const showAlert = (msg: string, kind: 'error' | 'success' = 'error') => {
    alertBox.className = kind === 'success' ? 'alert success' : 'alert';
    alertBox.textContent = msg;
    alertBox.removeAttribute('hidden');
    if (kind === 'success') {
      setTimeout(() => alertBox.setAttribute('hidden', ''), 4000);
    }
  };

  const markDirty = () => {
    dirty = true;
    saveBtn.removeAttribute('disabled');
  };

  const renderItem = (
    section: EditorSection,
    item: EditorItem,
    idx: number,
  ): HTMLElement => {
    const filenameInput = el('input', {
      class: 'mono',
      attrs: {
        type: 'text',
        placeholder: 'filename.safetensors',
        value: item.filename,
        spellcheck: false,
        'aria-label': 'Filename',
      },
    });
    // ── URL vs Local source toggle ──────────────────────────
    // Each row carries either a public URL (curl-fetched at build time) or a
    // local://<name> reference to a privately-uploaded model that gets COPY'd
    // into the image from the deploy-app's local store. The toggle swaps the
    // input control: a URL <input> for one, a <select> dropdown of uploads
    // for the other. Source state lives only on the item (derived from the
    // url prefix) — no separate field to keep in sync on save.
    let source: ItemSource = urlSource(item.url);

    const urlInput = el('input', {
      class: 'mono',
      attrs: {
        type: 'text',
        placeholder: 'https://huggingface.co/<org>/<repo>/resolve/main/<file>',
        value: source === 'url' ? item.url : '',
        spellcheck: false,
        'aria-label': 'URL',
      },
    });
    const localSelect = el('select', {
      class: 'mono',
      attrs: { 'aria-label': 'Local model' },
    });
    // Tracks the selected local model name even when the dropdown is hidden,
    // so flipping the toggle back and forth doesn't drop the selection.
    let selectedLocalName = source === 'local' ? localNameFromUrl(item.url) : '';

    const refreshLocalSelect = () => {
      localSelect.replaceChildren();
      const placeholder = el('option', {
        attrs: { value: '' },
        text: localModels.length === 0
          ? '— no local models uploaded yet —'
          : '— pick a local model —',
      });
      localSelect.append(placeholder);
      for (const m of localModels) {
        localSelect.append(el('option', {
          attrs: { value: m.name, title: `${m.folder}/${m.originalFilename} · ${formatBytes(m.sizeBytes)}` },
          text: `${m.name}  (${m.folder}/${m.originalFilename}, ${formatBytes(m.sizeBytes)})`,
        }));
      }
      // Pre-select the previously chosen local model if it still exists; on
      // a stale selection (model deleted server-side), surface a marker so
      // the user notices and re-picks rather than silently shipping an
      // invalid local:// reference.
      if (selectedLocalName) {
        const exists = localModels.some((m) => m.name === selectedLocalName);
        if (exists) {
          localSelect.value = selectedLocalName;
        } else {
          const orphan = el('option', {
            attrs: { value: selectedLocalName, selected: true },
            text: `${selectedLocalName}  (missing — re-upload or pick another)`,
          });
          localSelect.append(orphan);
          localSelect.value = selectedLocalName;
        }
      }
    };
    refreshLocalSelect();

    const localRefreshBtn = el('button', {
      class: 'icon-btn ghost',
      text: '↻',
      attrs: {
        type: 'button',
        'aria-label': 'Reload local models',
        title: 'Reload local models',
      },
      on: {
        click: async () => {
          localRefreshBtn.setAttribute('disabled', '');
          await reloadLocalModels();
          refreshLocalSelect();
          localRefreshBtn.removeAttribute('disabled');
        },
      },
    });

    const sourceBtn = el('button', {
      class: `icon-btn source-toggle source-${source}`,
      attrs: {
        type: 'button',
        'aria-label': `Source: ${source}`,
        title: source === 'local'
          ? 'Local upload — click to switch to URL'
          : 'Public URL — click to switch to local upload',
      },
      text: source === 'local' ? '🗄' : '🌐',
    });
    const sourceLabel = el('span', {
      class: 'source-label',
      text: source === 'local' ? 'Local' : 'URL',
    });

    // Wraps both URL input and Local select; we toggle their visibility
    // when the source switches so layout stays stable.
    const inputWrap = el('div', {
      class: 'models-editor-source-input',
      children: [urlInput, localSelect, localRefreshBtn],
    });
    const applySourceVisibility = () => {
      if (source === 'local') {
        urlInput.style.display = 'none';
        localSelect.style.display = '';
        localRefreshBtn.style.display = '';
      } else {
        urlInput.style.display = '';
        localSelect.style.display = 'none';
        localRefreshBtn.style.display = 'none';
      }
      sourceBtn.className = `icon-btn source-toggle source-${source}`;
      sourceBtn.textContent = source === 'local' ? '🗄' : '🌐';
      sourceBtn.setAttribute(
        'title',
        source === 'local'
          ? 'Local upload — click to switch to URL'
          : 'Public URL — click to switch to local upload',
      );
      sourceBtn.setAttribute('aria-label', `Source: ${source}`);
      sourceLabel.textContent = source === 'local' ? 'Local' : 'URL';
    };
    applySourceVisibility();

    sourceBtn.addEventListener('click', () => {
      source = source === 'local' ? 'url' : 'local';
      if (source === 'local') {
        // Volume + local don't combine: local files are baked into the
        // image. Force location='image' to keep the saved entry valid.
        item.location = 'image';
        // Re-emit the local:// URL so the saved entry stays consistent if
        // the user clicks save without picking a new model (selectedLocalName
        // may still be set from a prior session).
        item.url = selectedLocalName ? `${LOCAL_PREFIX}${selectedLocalName}` : '';
      } else {
        // Restore the URL input's current value (may be empty).
        item.url = urlInput.value.trim();
      }
      applySourceVisibility();
      markDirty();
    });

    const removeBtn = el('button', {
      class: 'icon-btn danger',
      text: '×',
      attrs: { type: 'button', 'aria-label': 'Remove model', title: 'Remove model' },
    });

    filenameInput.addEventListener('input', () => {
      item.filename = filenameInput.value.trim();
      markDirty();
    });
    urlInput.addEventListener('input', () => {
      const v = urlInput.value.trim();
      // Smart paste: when filename is empty and URL ends with a recognisable
      // model file basename, auto-fill the filename for fluidity. The stop
      // character class deliberately includes `)` and `]` so a markdown link
      // like `[file.py](http://file.py)` extracts just `file.py`, not `file.py)`.
      if (!filenameInput.value.trim()) {
        const match = v.match(
          /\/([A-Za-z0-9._-]+\.(?:safetensors|gguf|pth|bin|pt|onnx|ckpt|py))(?:[?#)\]]|$)/i,
        );
        if (match) {
          filenameInput.value = match[1]!;
          item.filename = match[1]!;
        }
      }
      item.url = v;
      markDirty();
    });
    localSelect.addEventListener('change', () => {
      const name = localSelect.value;
      selectedLocalName = name;
      item.url = name ? `${LOCAL_PREFIX}${name}` : '';
      // Auto-fill filename + sync the section's folder to the uploaded
      // model's default folder, since the user typically wants the file
      // placed exactly where the upload tab said it belongs. Keep
      // filename if the user already typed one (don't clobber edits).
      const picked = localModels.find((m) => m.name === name);
      if (picked) {
        if (!filenameInput.value.trim()) {
          filenameInput.value = picked.originalFilename;
          item.filename = picked.originalFilename;
        }
        if (picked.folder && picked.folder !== section.folder) {
          // Move this item to the section that matches the model's folder
          // (creating it if needed). Done after current event loop so the
          // mutation doesn't fight with this 'change' handler's redraw.
          requestAnimationFrame(() => moveItemToFolder(section, item, picked.folder));
        }
      }
      markDirty();
    });
    removeBtn.addEventListener('click', () => {
      section.items.splice(idx, 1);
      rerender();
      markDirty();
    });

    // Metadata block (display name / description / trigger words) — folded
    // by default to keep the dense grid readable, expanded on demand. Auto-
    // expand items that already carry metadata so the user sees what's there.
    const hasMeta = !!(item.displayName || item.description || item.triggerWords);
    const metaWrap = el('div', { class: 'models-editor-meta' });
    metaWrap.hidden = !hasMeta;

    const displayInput = el('input', {
      attrs: {
        type: 'text',
        placeholder: 'Display name (optional, shown in blueeden dropdowns)',
        value: item.displayName,
        'aria-label': 'Display name',
      },
    });
    displayInput.addEventListener('input', () => {
      item.displayName = displayInput.value;
      markDirty();
    });

    const triggerInput = el('input', {
      attrs: {
        type: 'text',
        placeholder: 'Trigger words (comma-separated, LoRAs)',
        value: item.triggerWords,
        'aria-label': 'Trigger words',
      },
    });
    triggerInput.addEventListener('input', () => {
      item.triggerWords = triggerInput.value;
      markDirty();
    });

    const descriptionInput = el('textarea', {
      attrs: {
        placeholder: 'Description — value range, compatibility, runtime context…',
        rows: 2,
        'aria-label': 'Description',
      },
    });
    descriptionInput.value = item.description;
    descriptionInput.addEventListener('input', () => {
      item.description = descriptionInput.value;
      markDirty();
    });

    metaWrap.append(displayInput, triggerInput, descriptionInput);

    const metaToggle = el('button', {
      class: 'icon-btn ghost',
      text: hasMeta ? '–' : '+',
      attrs: {
        type: 'button',
        'aria-label': 'Toggle metadata fields',
        title: 'Edit display name, description, trigger words',
      },
      on: {
        click: () => {
          metaWrap.hidden = !metaWrap.hidden;
          metaToggle.textContent = metaWrap.hidden ? '+' : '–';
        },
      },
    });

    // Per-model location toggle — flips between baking the file into the
    // image vs uploading it to the network volume on demand. UX is a single
    // pill button that swaps state + colour on click; clearer than a dropdown
    // for a binary choice and keeps the dense grid layout intact. Disabled
    // for local entries: the volume upload pod has no access to the
    // deploy-app's private store, so local files can only live in the image.
    const locationBtn = el('button', {
      class: `icon-btn location-toggle location-${item.location}`,
      attrs: {
        type: 'button',
        title:
          item.location === 'volume'
            ? 'On network volume — click to bake into image'
            : 'In Docker image — click to ship via network volume',
        'aria-label': `Location: ${item.location}`,
      },
      text: item.location === 'volume' ? '☁' : '◉',
    });
    const updateLocationBtnDisabledState = () => {
      if (source === 'local') {
        locationBtn.setAttribute('disabled', '');
        locationBtn.setAttribute('title', 'Local files always live in the Docker image');
      } else {
        locationBtn.removeAttribute('disabled');
      }
    };
    updateLocationBtnDisabledState();
    locationBtn.addEventListener('click', () => {
      if (source === 'local') return; // disabled for local entries
      item.location = item.location === 'volume' ? 'image' : 'volume';
      locationBtn.className = `icon-btn location-toggle location-${item.location}`;
      locationBtn.textContent = item.location === 'volume' ? '☁' : '◉';
      locationBtn.setAttribute(
        'title',
        item.location === 'volume'
          ? 'On network volume — click to bake into image'
          : 'In Docker image — click to ship via network volume',
      );
      locationBtn.setAttribute('aria-label', `Location: ${item.location}`);
      markDirty();
    });
    // Hook the source toggle to also refresh the location button's enabled
    // state — sourceBtn already swaps the input control, this adds the
    // location-button disable as a side-effect.
    sourceBtn.addEventListener('click', updateLocationBtnDisabledState);

    return el('div', {
      class: 'models-editor-item-wrap',
      children: [
        el('div', {
          class: 'models-editor-item',
          children: [filenameInput, sourceBtn, sourceLabel, inputWrap, metaToggle, locationBtn, removeBtn],
        }),
        metaWrap,
      ],
    });
  };

  /**
   * Relocates `item` from `currentSection` to the section matching `targetFolder`,
   * creating an empty section if none exists yet. Used by the local-model
   * picker to auto-place a freshly-picked upload into the right ComfyUI
   * subfolder so the on-disk path matches the upload's declared folder.
   */
  const moveItemToFolder = (
    currentSection: EditorSection,
    item: EditorItem,
    targetFolder: string,
  ): void => {
    if (currentSection.folder === targetFolder) return;
    const fromIdx = currentSection.items.indexOf(item);
    if (fromIdx < 0) return;
    currentSection.items.splice(fromIdx, 1);
    let target = sections.find((s) => s.folder === targetFolder);
    if (!target) {
      target = { folder: targetFolder, items: [] };
      sections.push(target);
    }
    target.items.push(item);
    rerender();
    markDirty();
  };

  const renderSection = (section: EditorSection, sectionIdx: number): HTMLElement => {
    const folderInput = el('input', {
      class: 'mono models-editor-folder',
      attrs: {
        type: 'text',
        value: section.folder,
        spellcheck: false,
        'aria-label': 'Folder name',
        title: '/ComfyUI/models/<folder>',
      },
    });
    folderInput.addEventListener('input', () => {
      section.folder = folderInput.value.trim();
      markDirty();
    });

    const removeSectionBtn = el('button', {
      class: 'icon-btn danger',
      text: '×',
      attrs: { type: 'button', 'aria-label': 'Remove section', title: 'Remove this section' },
      on: {
        click: () => {
          if (section.items.length > 0) {
            if (!confirm(`Remove "${section.folder}" and its ${section.items.length} model(s)?`)) {
              return;
            }
          }
          sections.splice(sectionIdx, 1);
          rerender();
          markDirty();
        },
      },
    });

    const itemsHost = el('div', { class: 'models-editor-items' });
    section.items.forEach((item, i) => {
      itemsHost.append(renderItem(section, item, i));
    });

    const addBtn = el('button', {
      class: 'ghost',
      text: '+ Add model',
      attrs: { type: 'button' },
      on: {
        click: () => {
          section.items.push({
            filename: '', url: '', displayName: '', description: '', triggerWords: '',
            location: 'image',
          });
          rerender();
          markDirty();
          // Focus the new URL input for fluid pasting.
          requestAnimationFrame(() => {
            const inputs = sectionsHost.querySelectorAll<HTMLInputElement>(
              `[data-section-idx="${sectionIdx}"] input.mono`,
            );
            const lastUrl = inputs[inputs.length - 1];
            lastUrl?.focus();
          });
        },
      },
    });

    const volumeCount = section.items.filter((i) => i.location === 'volume').length;
    const imageCount = section.items.length - volumeCount;
    const countLabel = volumeCount > 0
      ? `${imageCount} in image · ${volumeCount} on volume`
      : section.items.length === 1
        ? '1 model'
        : `${section.items.length} models`;

    return el('div', {
      class: 'models-editor-section',
      attrs: { 'data-section-idx': String(sectionIdx) },
      children: [
        el('div', {
          class: 'models-editor-section-head',
          children: [
            el('span', { class: 'models-editor-prefix', text: '/ComfyUI/models/' }),
            folderInput,
            el('span', { class: 'who', text: countLabel }),
            el('span', { class: 'spacer' }),
            removeSectionBtn,
          ],
        }),
        itemsHost,
        el('div', { class: 'models-editor-section-foot', children: [addBtn] }),
      ],
    });
  };

  const rerender = () => {
    sectionsHost.replaceChildren(...sections.map(renderSection));
    if (sections.length === 0) {
      sectionsHost.append(
        el('div', {
          class: 'empty',
          text: 'No sections yet. Click "+ Add section" to get started.',
        }),
      );
    }
  };

  addSectionBtn.addEventListener('click', () => {
    const name = window.prompt(
      'New section folder (under /ComfyUI/models/) — e.g. checkpoints, RMBG/BEN2',
    );
    if (!name) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    if (sections.some((s) => s.folder === trimmed)) {
      showAlert(`Section "${trimmed}" already exists`);
      return;
    }
    sections.push({ folder: trimmed, items: [] });
    rerender();
    markDirty();
  });

  const close = () => {
    if (dirty && !confirm('Discard unsaved changes?')) return;
    backdrop.remove();
    document.removeEventListener('keydown', escHandler);
  };
  cancelBtn.addEventListener('click', close);
  backdrop.addEventListener('click', (ev) => {
    if (ev.target === backdrop) close();
  });
  function escHandler(ev: KeyboardEvent) {
    if (ev.key === 'Escape') close();
  }
  document.addEventListener('keydown', escHandler);

  saveBtn.addEventListener('click', async () => {
    // Client-side validation — surface the first issue and bail out.
    for (const s of sections) {
      if (!s.folder) {
        showAlert('A section is missing a folder name');
        return;
      }
      for (const item of s.items) {
        if (!item.filename || !item.url) {
          showAlert(`Section "${s.folder}" has an incomplete model (missing filename or pick)`);
          return;
        }
        if (item.url.startsWith(LOCAL_PREFIX)) {
          const name = item.url.slice(LOCAL_PREFIX.length);
          if (!/^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(name)) {
            showAlert(`Local model name for "${item.filename}" is invalid`);
            return;
          }
          if (!localModels.some((m) => m.name === name)) {
            showAlert(
              `Local model "${name}" not found — re-upload it on the Custom Models tab or pick another`,
            );
            return;
          }
        } else if (!/^https?:\/\//i.test(item.url)) {
          showAlert(`URL for "${item.filename}" must start with http(s):// or be a local upload`);
          return;
        }
      }
    }
    saveBtn.setAttribute('disabled', '');
    saveBtn.textContent = 'Saving…';
    try {
      const payload = {
        baseSha,
        sections: sections
          // Drop empty sections silently — the user added them and changed
          // their mind, no need to save an empty mkdir.
          .filter((s) => s.items.length > 0)
          .map((s) => ({
            folder: s.folder,
            items: s.items.map(
              (i): RunpodModelEntry => {
                // Only include metadata fields when set — this keeps the saved
                // payload (and the resulting Dockerfile diff) minimal.
                const dn = i.displayName.trim();
                const tw = i.triggerWords.trim();
                const desc = i.description.trim();
                return {
                  filename: i.filename,
                  url: i.url,
                  ...(dn ? { displayName: dn } : {}),
                  ...(tw ? { triggerWords: tw } : {}),
                  ...(desc ? { description: desc } : {}),
                  ...(i.location === 'volume' ? { location: i.location } : {}),
                };
              },
            ),
          })) satisfies RunpodModelSection[],
        cudaArchitectures: cudaInput.value.trim(),
      };
      const res = await deps.api.saveRunpodModels(deps.branch, payload);
      if (res.unchanged) {
        showAlert('No changes to commit', 'success');
      } else {
        showAlert(
          `Committed: ${res.message ?? 'updated'} (${res.commitSha?.slice(0, 7) ?? '?'})`,
          'success',
        );
        deps.onSaved?.({
          ...(res.commitSha !== undefined ? { commitSha: res.commitSha } : {}),
          ...(res.message !== undefined ? { message: res.message } : {}),
        });
      }
      baseSha = res.baseSha;
      dirty = false;
      saveBtn.textContent = 'Save';
      // Stay open so the user can keep editing on the same head.
    } catch (err) {
      saveBtn.textContent = 'Save';
      saveBtn.removeAttribute('disabled');
      if (err instanceof ApiError && err.code === 'stale_base_sha') {
        if (confirm('The Dockerfile changed on GitHub since you opened the editor. Reload from upstream and lose your edits?')) {
          await load();
        }
        return;
      }
      showAlert(err instanceof ApiError ? err.message : 'Save failed');
    }
  });

  const load = async () => {
    try {
      // Fetch the Dockerfile state and the local-model catalogue in parallel
      // so the per-row Local picker has data ready as soon as the editor
      // renders. Failure of the local-models fetch is non-fatal — handled
      // inside reloadLocalModels.
      const [data] = await Promise.all([
        deps.api.getRunpodModels(deps.branch),
        reloadLocalModels(),
      ]);
      baseSha = data.baseSha;
      cudaInput.value = data.cudaArchitectures ?? '';
      // Preserve the order from the Dockerfile so subsequent saves don't
      // produce cosmetic-only diffs. Append the canonical defaults (empty)
      // at the bottom for any branch that doesn't already have them — that
      // way the user always sees the standard ComfyUI folders ready to fill.
      const seen = new Set<string>();
      sections = [];
      for (const s of data.sections) {
        sections.push({
          folder: s.folder,
          items: s.items.map((i) => ({
            filename: i.filename,
            url: i.url,
            displayName: i.displayName ?? '',
            description: i.description ?? '',
            triggerWords: i.triggerWords ?? '',
            location: (i.location ?? 'image') as RunpodModelLocation,
          })),
        });
        seen.add(s.folder);
      }
      for (const f of data.emptyFolders) {
        if (!seen.has(f)) {
          sections.push({ folder: f, items: [] });
          seen.add(f);
        }
      }
      for (const f of DEFAULT_FOLDERS) {
        if (!seen.has(f)) {
          sections.push({ folder: f, items: [] });
          seen.add(f);
        }
      }
      const total = sections.reduce((acc, s) => acc + s.items.length, 0);
      subtitleEl.textContent = `${total} model(s) · file sha ${baseSha.slice(0, 7)}${data.notFound ? ' · no model block found, will be created on save' : ''}`;
      dirty = false;
      saveBtn.setAttribute('disabled', '');
      saveBtn.textContent = 'Save';
      rerender();
    } catch (err) {
      subtitleEl.textContent = 'failed to load';
      showAlert(err instanceof ApiError ? err.message : 'Failed to load models');
    }
  };

  void load();
}
